import React = require("react");

import {MarketingConfig} from "../data/MarketingConfig";
import {MarketingData} from "../data/MarketingData";
import {MarketingTabMapper} from "../utils/MarketingTabMapper";
import {AssetBaseWidget} from "../widgets/AssetBaseWidget";
import {HistogramChartWidget} from "../widgets/HistogramChartWidget";
import {LargeHistogramChartWidget} from "../widgets/LargeHistogramChartWidget";
import {LargeHistogramChartWidget2Axis} from "../widgets/LargeHistogramChartWidget2Axis";
import {LargeHistogramGroupChartWidget} from "../widgets/LargeHistogramGroupChartWidget";
import {LocationsTrendWidget} from "../widgets/LocationsTrendWidget";
import {MarketingSalesWidget} from "../widgets/MarketingSalesWidget";
import {MarketingSalesWithBestWidget} from "../widgets/MarketingSalesWithBestWidget";

import UnitOfMeasure = MarketingConfig.UnitOfMeasure;
import MarketingDataConverter = MarketingData.MarketingDataConverter;

interface MarketingTabRollingYearProps {
    configuration: MarketingConfig.Container;
    dataRollingYear: MarketingData.RollingPeriodWithMonthlySales;
    goToLocation: () => any;
    unitOfMeasure: UnitOfMeasure;
    isDetailLevel: boolean;
}

/**
 * Rolling year marketing report.
 */
export class MarketingTabRollingYear extends React.Component<MarketingTabRollingYearProps, {}> {

    constructor(props: MarketingTabRollingYearProps) {
        super(props);
    }

    public render() {
        return (
            <div className="marketing-tab__widgets">
                {this.getWidgets()}
            </div>
        );
    }

    private getWidgets(): JSX.Element[] {
        const unitOfMeasure = this.props.unitOfMeasure;
        const config = this.props.configuration;
        const data: MarketingData.RollingPeriodWithMonthlySales = JSON.parse(
            JSON.stringify(this.props.dataRollingYear),
        );
        const assetsConfig = config.assets;
        const assetsData = data.assets;
        const bestLocations = MarketingTabMapper.locationTrendRows(data.bestLocations, unitOfMeasure);
        const bestProduct = MarketingTabMapper.bestProduct(data.productSales, unitOfMeasure);
        const productSalesRows = MarketingTabMapper.productSaleRowMapper(
            data.productSales.productSaleRows,
            unitOfMeasure,
        );
        const dayIntervalSalesRows = MarketingTabMapper.dayIntervalSaleRowMapper(
            data.dayIntervalSales,
            unitOfMeasure,
        );
        const weekdaySales = MarketingTabMapper.weekdaySaleRowMapper(
            data.weekdaySales,
            unitOfMeasure,
        );
        const monthSales = MarketingTabMapper.monthSaleRowMapper(
            data.monthSales,
            unitOfMeasure,
        );
        const productSelectionAverage = MarketingTabMapper.productSelectionAverageMonthToDateMapper(
            data.productSelectionsAverages,
            unitOfMeasure,
        );
        const weekdayDayIntervalSales = MarketingTabMapper.weekDayDayIntervalSalesMapper(
            data.weekdayDayIntervalSales,
            unitOfMeasure,
        );
        const assetsAverages = MarketingTabMapper.assetsAverageRowMapper(
            data.assetsAverages,
            config.salesAverages.locationAverageTitle,
            config.salesAverages.equipmentAverageTitle,
            config.salesAverages.selectionAverageTitle,
            unitOfMeasure,
        );
        const result = [];
        result.push(
            (
                <div
                    className={"marketing-2x2-widgets-container "}
                >
                    <AssetBaseWidget
                        title={assetsConfig.title}
                        locations={{name: assetsConfig.locationsTitle, count: assetsData.locations}}
                        telemetryKits={{name: assetsConfig.telemetriesTitle, count: assetsData.telemetries}}
                        selections={{name: assetsConfig.selectionsTitle, count: assetsData.selections}}
                        unitOfMeasure={unitOfMeasure}
                    />
                    <MarketingSalesWidget
                        period={null}
                        rollingPeriod={config.periods.rollingYear}
                        title={config.salesTitle}
                        titleSalesTotals={config.salesTotals.title}
                        titleLocationAverage={config.salesAverages.locationAverageTitle}
                        titleSelectionAverage={config.salesAverages.selectionAverageTitle}
                        titleEquipmentAverage={config.salesAverages.equipmentAverageTitle}
                        unitOfMeasure={unitOfMeasure}
                        salesRollingPeriod={{
                            title: config.salesTotals.title,
                            quantity: MarketingDataConverter.chooseAndConvertValue(
                                data.sales,
                                data.consumptions,
                                unitOfMeasure,
                            ),
                            unitOfMeasure: unitOfMeasure.symbol,
                        }}
                        locationAvgRollingPeriod={{
                            title: config.salesAverages.locationAverageTitle,
                            quantity: MarketingDataConverter.chooseAndConvertValue(
                                data.salesLocationAverages,
                                data.consumptionsLocationAverages,
                                unitOfMeasure,
                            ),
                            unitOfMeasure: unitOfMeasure.symbol,
                        }}
                        selectionAvgRollingPeriod={{
                            title: config.salesAverages.selectionAverageTitle,
                            quantity: MarketingDataConverter.chooseAndConvertValue(
                                data.salesSelectionAverages,
                                data.consumptionsSelectionAverages,
                                unitOfMeasure,
                            ),
                            unitOfMeasure: unitOfMeasure.symbol,
                        }}
                        equipmentAvgRollingPeriod={{
                            title: config.salesAverages.equipmentAverageTitle,
                            quantity: MarketingDataConverter.chooseAndConvertValue(
                                data.salesEquipmentAverages,
                                data.consumptionsEquipmentAverages,
                                unitOfMeasure,
                            ),
                            unitOfMeasure: unitOfMeasure.symbol,
                        }}
                    />
                </div>
            ),
        );
        if (this.props.configuration.isDeprecatedCompany2) {
            if (!this.props.isDetailLevel) {
                result.push(
                    (
                        <LocationsTrendWidget
                            title={config.locationsRanks.title}
                            bestLocationsData={bestLocations}
                            goToLocation={this.props.goToLocation}
                            unitOfMeasure={unitOfMeasure.symbol}
                            wide={true}
                        />
                    ),
                );
            }
        } else {
            result.push(
                (
                    <MarketingSalesWithBestWidget
                        title={config.productSales.title}
                        bestSaleEntityData={
                            {
                                title: config.productSales.bestProductTitle,
                                name: bestProduct.productName,
                                percentageOfTotal: bestProduct.percentage,
                                quantity: bestProduct.value,
                                unitOfMeasure: unitOfMeasure.symbol,
                                color: MarketingTabMapper.getProductColor(
                                    bestProduct.productName,
                                    data.productSales.productSaleRows,
                                ),
                            }
                        }
                        chartDataSet={productSalesRows}
                        wide={true}
                    />
                ),
            );
            if (!this.props.isDetailLevel) {
                result.push(
                    (
                        <LocationsTrendWidget
                            title={config.locationsRanks.title}
                            bestLocationsData={bestLocations}
                            goToLocation={this.props.goToLocation}
                            unitOfMeasure={unitOfMeasure.symbol}
                            wide={false}
                        />
                    ),
                );
            }
        }
        if (!MarketingDataConverter.isUnitsUnitOfMeasure(this.props.unitOfMeasure)) {
            result.push(
                (
                    <LargeHistogramChartWidget2Axis
                        title={config.productSelectionsAveragesTitle}
                        data={productSelectionAverage}
                        yUnitOfMeasure={unitOfMeasure.name}
                        y1UnitOfMeasure={config.assets.selectionsTitle}
                    />
                ),
            );
        }
        const dayIntervalSalesWidget = (
            <LargeHistogramChartWidget
                title={config.dayIntervalSales.title}
                data={dayIntervalSalesRows}
                yUnitOfMeasure={unitOfMeasure.name}
            />
        );
        const weekDaySalesWidget = (
            <HistogramChartWidget
                title={config.weekdaySalesTitle}
                data={weekdaySales}
                yUnitOfMeasure={unitOfMeasure.name}
            />
        );
        if (this.props.isDetailLevel && !MarketingDataConverter.isUnitsUnitOfMeasure(unitOfMeasure)) {
            result.push(
                weekDaySalesWidget,
            );
            result.push(
                dayIntervalSalesWidget,
            );
        } else {
            result.push(
                dayIntervalSalesWidget,
            );
            result.push(
                weekDaySalesWidget,
            );
        }
        result.push(
            (
                <LargeHistogramGroupChartWidget
                    title={config.weekDayDayIntervalSalesTitle}
                    data={weekdayDayIntervalSales}
                    yUnitOfMeasure={unitOfMeasure.name}
                />
            ),
        );
        if (MarketingDataConverter.isUnitsUnitOfMeasure(unitOfMeasure)) {
            result.push(
                (
                    <HistogramChartWidget
                        title={config.monthSalesTitle}
                        data={monthSales}
                        yUnitOfMeasure={unitOfMeasure.name}
                    />
                ),
            );
        } else {
            result.push(
                (
                    <LargeHistogramChartWidget2Axis
                        title={config.monthSalesTitle}
                        data={monthSales}
                        yUnitOfMeasure={unitOfMeasure.name}
                        y1UnitOfMeasure={config.assets.selectionsTitle}
                    />
                ),
            );
        }
        result.push(
            (
                <LargeHistogramGroupChartWidget
                    title={config.assetsAverageMonthTitle}
                    data={assetsAverages}
                    yUnitOfMeasure={unitOfMeasure.name}
                />
            ),
        );
        return result;
    }
}
